import cloneDeep from "lodash.clonedeep";
import get from "lodash.get";
import set from "lodash.set";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { ACTIONS, MODALS, QUOTE_TYPES } from "../../constants";
import { currencyFormatter } from "../../formatters";
import ajv from "../../helpers/ajv";
import { resolveSchema } from "../../helpers/resolveSchema";
import Banner from "../components/Banner";
import Button from "../components/Button";
import Form from "../components/Form";
import InputCheckbox from "../components/InputCheckbox";
import Modal from "../components/Modal";
import Table from "../components/Table";
import mapChildren from "../helpers/mapChildren";
import { useContractQuery, useQuoteQuery } from "../hooks";

const { Body, Row, Data } = Table;

const getPremiumChange = ({ quoteData }) => {
  if (quoteData?.base?.refund_premium) {
    return quoteData?.base?.refund_premium;
  }

  if (quoteData?.base?.additional_premium) {
    return quoteData?.base?.additional_premium;
  }

  return { amount: 0, currency: "USD" };
};

const getPremiumChangeLabel = ({ quoteData, value }) => {
  if (quoteData?.base?.additional_premium || value?.amount === 0) {
    return "Amount due";
  }

  return "Amount owed";
};

const ModalForm = ({ callback, handleClose, quoteData, schemaData, endorsementData }) => {
  const {
    contractId,
    endorsementId,
    isUpdating,
    productRef,
    quoteId,
    updateQuote,
  } = useQuoteQuery();
  const [formValues, setFormValues] = useState(quoteData.base);
  const [isBindConfirmed, setBindConfirmed] = useState(false);
  const clonedSchema = cloneDeep(schemaData);
  const resolvedSchema = resolveSchema(clonedSchema, formValues);
  const clonedValues = cloneDeep(formValues);
  const validate = ajv.compile(schemaData);
  const isValid = validate(clonedValues);

  if (quoteData.type === QUOTE_TYPES.COMMERCIAL) {
    set(resolvedSchema, "properties.effective_from.ui:component:readonly", true);
  }

  const handleChange = (...args) => {
    const clonedFormValues = cloneDeep(formValues);

    if (args.length === 1) {
      const [event] = args;

      set(clonedFormValues, event.target.name, event.target.value);
    }

    if (args.length === 2) {
      const [value, name] = args;

      set(clonedFormValues, name, value);
    }

    setFormValues(clonedFormValues);
  };

  const handleSubmit = async () => {
    const res = await updateQuote({
      contractId,
      data: { type: ACTIONS.BIND_QUOTE, payload: { binding: formValues } },
      endorsementId,
      productRef,
      quoteId,
    });

    handleClose();
    callback(get(res, "data.data"));
  };

  const premiumChange = getPremiumChange({ endorsementData, quoteData });
  const premiumChangeAbs = { amount: Math.abs(premiumChange.amount) };

  return (
    <div className="p-6">
      <Table className="mb-8">
        <Body>
          <Row className="border-b border-gray-500 last:border-0">
            <Data>Limit required</Data>
            <Data>{currencyFormatter(quoteData.base.limit_required)}</Data>
          </Row>

          <Row className="border-b border-gray-500 last:border-0">
            <Data>Deductible</Data>
            <Data>{currencyFormatter(quoteData.base.deductible)}</Data>
          </Row>

          <Row className="border-b border-gray-500 last:border-0">
            <Data className="font-bold">
              {getPremiumChangeLabel({ quoteData, value: premiumChange })}
            </Data>
            <Data className="font-bold">{currencyFormatter(premiumChangeAbs)}</Data>
          </Row>
        </Body>
      </Table>

      <Form onSubmit={handleSubmit}>
        {mapChildren({
          ctx: { contractData: endorsementData },
          formValues,
          onChange: handleChange,
          parentKey: "",
          parentSchema: resolvedSchema,
          setFormValues,
          validationErrors: validate.errors,
        })}

        {isValid && (
          <Banner
            key={formValues.inceptionDate}
            className="mb-8"
            color="orange"
            headingText="Are you sure you want to bind this endorsement? This cannot be undone."
          >
            <InputCheckbox
              labelText=" I understand"
              name="confirm_bind"
              onChange={() => setBindConfirmed(!isBindConfirmed)}
              isChecked={isBindConfirmed}
            />
          </Banner>
        )}

        <div className="flex">
          <Button
            className="mr-4"
            isDisabled={!isValid || !isBindConfirmed || isUpdating}
            kind="primary"
            type="submit"
          >
            Bind endorsement
          </Button>

          <Button onClick={handleClose}>Cancel</Button>
        </div>
      </Form>
    </div>
  );
};

const BindEndoQuoteModal = ({ handleClose, callback }) => {
  const { quoteData, isLoading: isQuoteLoading, schemaData } = useQuoteQuery();
  const { endorsementData, isLoading: isContractLoading } = useContractQuery();
  const isLoading = isQuoteLoading || isContractLoading;

  return (
    <Modal
      handleClose={handleClose}
      headingText={!isLoading && "Bind endorsement"}
      isLoading={isLoading}
      clearParamsOnClose
    >
      <ModalForm
        callback={callback}
        endorsementData={endorsementData}
        handleClose={handleClose}
        quoteData={quoteData}
        schemaData={schemaData?.properties?.[MODALS.BIND_ENDORSEMENT_QUOTE]}
      />
    </Modal>
  );
};

BindEndoQuoteModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
};

export default BindEndoQuoteModal;
